import { InvalidTargetError } from '../utils/errors';
import { keysPressed } from '../globals/keys-pressed';

export class FocusRedirect {
  constructor(el, config) {
    this.el = el;
    this.updateConfig(config);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocusout = this.onFocusout.bind(this);
    this.isActive = false;
  }

  updateConfig({ next = {}, previous = {} }) {
    this.next = {};
    this.next.selector = next.selector || undefined;
    this.next.isDisabled = next.isDisabled || false;
    this.next.selectLastElement = next.selectLastElement || false;
    this.next.selectClosestElement = next.selectClosestElement || false;
    this.next.ifHasClass = next.ifHasClass || false;
    this.next.onFocusout = next.onFocusout || false;
    this.previous = {};
    this.previous.selector = previous.selector || undefined;
    this.previous.isDisabled = previous.isDisabled || false;
    this.previous.selectLastElement = previous.selectLastElement || false;
    this.previous.selectClosestElement = previous.selectClosestElement || false;
    this.previous.ifHasClass = previous.ifHasClass || false;
    this.previous.onFocusout = previous.onFocusout || false;
  }

  activate() {
    if (this.isActive) return;
    this.isActive = true;
    if (this.previous.onFocusout || this.next.onFocusout) {
      this.el.addEventListener('focusout', this.onFocusout);
      return;
    }
    this.el.addEventListener('keydown', this.onKeydown);
    this.el.addEventListener('keyup', this.onKeyup);
  }

  deactivate() {
    if (!this.isActive) return;
    this.isActive = false;
    this.el.removeEventListener('keydown', this.onKeydown);
    this.el.removeEventListener('keyup', this.onKeyup);
  }

  getDirection(event) {
    const direction = event.shiftKey ? 'previous' : 'next';
    return direction;
  }

  onKeydown(event) {
    if (event.key !== 'Tab') return;

    const direction = this.getDirection(event);
    const target = this[direction];

    if (!target || target.isDisabled) return;
    if (target.ifHasClass && !this.el.classList.contains(target.ifHasClass))
      return;

    this.redirect(
      target.selector,
      target.selectLastElement,
      target.selectClosestElement
    )
      .then(() => event.preventDefault())
      .catch(error => {
        if (error.name === 'InvalidTargetError') return;
        throw error;
      });
  }

  onFocusout(event) {
    if (!keysPressed.Tab) return;

    const direction = keysPressed.Shift ? 'previous' : 'next';
    const target = this[direction];

    if (!target || target.isDisabled) return;
    if (target.ifHasClass && !this.el.classList.contains(target.ifHasClass))
      return;

    if (target.onFocusout && !this.el.contains(event.relatedTarget)) {
      this.redirect(
        target.selector,
        target.selectLastElement,
        target.selectClosestElement
      )
        .then(() => event.preventDefault())
        .catch(error => {
          if (error.name === 'InvalidTargetError') return;
          throw error;
        });
    }
  }

  redirect(selector, selectLastElement, selectClosestElement) {
    return new Promise((resolve, reject) => {
      const splitSelector = selector
        ?.replaceAll('>', '') // remove specifity selector
        .replace(/\s+/g, ' ') // collapse multiple successive whitespaces to only one
        .split(' '); // split selector based on whitespace
      const firstEl = document.querySelector(selector);
      const allEl = document.querySelectorAll(selector);
      let closestEl = this.el.closest(selector);
      let el;

      // If no closest parent try for closest child
      if (!closestEl && splitSelector?.length > 1) {
        const mainSelector = this.el.closest(splitSelector[0]);
        closestEl = mainSelector;
        splitSelector.forEach((subSelector, index) => {
          if (index > 0) {
            closestEl = closestEl?.querySelector(subSelector);
          }
        });
      }

      el = firstEl;
      if (selectLastElement) el = allEl[allEl.length - 1];
      if (selectClosestElement) el = closestEl;

      if (!el || el.offsetParent === null)
        return reject(new InvalidTargetError());

      el.focus();
      resolve();
    });
  }
}
